import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAdminVideos,
  createVideo,
  updateVideo,
  deleteVideo,
} from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Play, Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminVideos() {
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    category: '',
    is_active: true
  });

  const queryClient = useQueryClient();

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['adminVideos'],
    queryFn: () => listAdminVideos(100)
  });

  const createMutation = useMutation({
    mutationFn: (data) => createVideo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminVideos'] });
      toast.success('Vídeo adicionado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateVideo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminVideos'] });
      toast.success('Vídeo atualizado!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteVideo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminVideos'] });
      toast.success('Vídeo excluído!');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      youtube_url: '',
      category: '',
      is_active: true
    });
    setEditingVideo(null);
    setShowForm(false);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title || '',
      description: video.description || '',
      youtube_url: video.youtube_url || '',
      category: video.category || '',
      is_active: video.is_active ?? true
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.youtube_url) {
      toast.error('Título e URL são obrigatórios.');
      return;
    }

    if (editingVideo) {
      updateMutation.mutate({ id: editingVideo.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  const categoryColors = {
    'Pregação': 'bg-indigo-100 text-indigo-700',
    'Louvor': 'bg-purple-100 text-purple-700',
    'Testemunho': 'bg-emerald-100 text-emerald-700',
    'Estudo': 'bg-amber-100 text-amber-700',
    'Evento': 'bg-rose-100 text-rose-700',
    'Outro': 'bg-gray-100 text-gray-700',
  };

  const toggleActive = (video) => {
    updateMutation.mutate({ 
      id: video.id, 
      data: { is_active: !video.is_active } 
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-red-500 to-rose-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Vídeos" 
            subtitle={`${videos.length} vídeos`}
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {videos.length === 0 ? (
          <EmptyState
            icon={Play}
            title="Nenhum vídeo cadastrado"
            description="Adicione vídeos do YouTube."
            actionLabel="Adicionar Vídeo"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {videos.map((video, index) => {
              const thumbnail = getYouTubeThumbnail(video.youtube_url);
              
              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-xl shadow-sm overflow-hidden ${!video.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="aspect-video relative">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <Play className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    {!video.is_active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-white px-3 py-1 rounded-full text-sm">Inativo</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">{video.title}</h3>
                      {video.category && (
                        <Badge className={categoryColors[video.category] || categoryColors['Outro']}>
                          {video.category}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={() => toggleActive(video)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                      >
                        {video.is_active ? (
                          <Eye className="w-4 h-4 text-green-600" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(video)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200"
                          onClick={() => {
                            if (confirm('Excluir este vídeo?')) {
                              deleteMutation.mutate(video.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingVideo ? 'Editar Vídeo' : 'Novo Vídeo'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Título do vídeo"
              />
            </div>

            <div>
              <Label>URL do YouTube *</Label>
              <Input
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
              />
              {formData.youtube_url && getYouTubeThumbnail(formData.youtube_url) && (
                <img
                  src={getYouTubeThumbnail(formData.youtube_url)}
                  alt="Preview"
                  className="w-full h-32 object-cover rounded-lg mt-2"
                />
              )}
            </div>

            <div>
              <Label>Categoria</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pregação">Pregação</SelectItem>
                  <SelectItem value="Louvor">Louvor</SelectItem>
                  <SelectItem value="Testemunho">Testemunho</SelectItem>
                  <SelectItem value="Estudo">Estudo</SelectItem>
                  <SelectItem value="Evento">Evento</SelectItem>
                  <SelectItem value="Culto">Culto</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Vídeo ativo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingVideo ? 'Salvar' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
